import React from 'react'
import Condition from 'components/Condition'
import MoreButtonCard from 'components/MoreButtonCard'
import { Badge, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Style, VerifiedUser } from '@mui/icons-material'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { useFragment, graphql } from 'react-relay'
import { BadgesArea_User$key } from '__generated__/BadgesArea_User.graphql'
import StatusField from '../../../fields/StatusField'
import AvailabilityAndPositionTypeFields from '../../../fields/AvailabilityAndPositionTypeFields'
import TeamManagementField from '../../../fields/TeamManagementField'
import USWorkField from '../../../fields/USWorkField'
import useStyles from './styles'

export interface IBadgesAreaProps {
  editable: boolean
  user: BadgesArea_User$key
}

function BadgesArea(props: IBadgesAreaProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment BadgesArea_User on User {
      backgroundCheckCompleted
      status
      
      profile {
        availability
        canWorkInTheUs
        managedTeamSize
      }

      ...AvailabilityAndPositionTypeFields_User
      ...StatusField_User
      ...TeamManagementField_User
      ...USWorkField_User
    }
  `, userFragmentRef)

  const backgroundCheckCompleted = user?.backgroundCheckCompleted
  const status = user?.status
  const availability = user?.profile?.availability
  const canWorkInTheUs = user?.profile?.canWorkInTheUs
  const managedTeamSize = user?.profile?.managedTeamSize

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  let count = 0

  if (backgroundCheckCompleted) count++
  if (managedTeamSize && managedTeamSize !== 'none') count++
  if (status === 'accepted') count++
  if (availability) count++
  if (canWorkInTheUs) count++

  const hasErrors = false // TODO: reimplement this
  const color = hasErrors ? 'secondary' : 'primary'

  // TODO: this should probably be its own component
  const innerBadgesArea = (
    <div className={classes.wrapper}>
      <div>
        <StatusField editable={editable} user={user} />
      </div>

      {backgroundCheckCompleted && (
        <div>
          <div className={classes.verified} data-cy="verification-badge-verified">
            <VerifiedUser className={classes.statusIcon} /> Background Check
          </div>
        </div>
      )}

      <div>
        <div>
          <AvailabilityAndPositionTypeFields editable={editable} user={user} />
        </div>
      </div>

      <div>
        <TeamManagementField editable={editable} user={user} />
      </div>

      <div>
        <USWorkField editable={editable} user={user} />
      </div>
    </div>
  )

  return (
    <Typography variant="body1" component="div" className={classes.badgeArea}>
      <Condition condition={!isMobile}>
        {innerBadgesArea}
      </Condition>

      <Condition condition={isMobile}>
        <Badge
          style={{ transform: 'translate(-9px, 9px)' }}
          badgeContent={count || ''}
          color={color}
        >
          <div style={{ transform: 'translate(9px, -9px)' }}>
            <MoreButtonCard icon={<Style />}>
              <div style={{ padding: theme.spacing(2) }}>
                {innerBadgesArea}
              </div>
            </MoreButtonCard>
          </div>
        </Badge>
        {hasErrors && <Typography variant="body2" className={classes.error}>Missing Info</Typography>}
      </Condition>
    </Typography>
  )
}

export default React.memo(BadgesArea)
